import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMe } from "../../../../swr/useMe";
import { Text } from "../../../../components/common/typography";
import { AuthLayout } from "../../../authentication/routes/auth-layout";
import {
  hasAllDocumentsUploaded,
  getMissingDocuments,
  REQUIRED_DOCUMENTS,
} from "../../../../utils/onboarding";
import { Loader } from "../../../../components/common/loader";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { UploadedDocumentsSection } from "../components/uploaded-documents-section";
import { RequiredDocumentsSection } from "../components/required-documents-section";
import { useDocumentUpload } from "../../hooks/useDocumentUpload";
import { useDocuments } from "../../../settings/hooks/useDocuments";
import { DocumentsSection } from "../../../settings/presentation/components/documents_section";
import { useLogout } from "../../../authentication/hooks/useLogout";
import { Button } from "../../../../components/common/button";
import { DocumentDtoStatusEnum, Role } from "../../../../api";
import { showSuccessToast } from "../../../../utils/toasts";

export function OnboardingPage() {
  const { user, isLoading, mutate } = useMe();
  const navigate = useNavigate();
  const handleLogout = useLogout();

  const {
    data: documentsData,
    isLoading: isLoadingDocuments,
    mutate: mutateDocuments,
  } = useDocuments();

  const {
    uploading,
    uploadProgress,
    selectedFiles,
    handleFileSelect,
    handleFileRemove,
    handleConfirmUpload,
  } = useDocumentUpload({
    onUploadSuccess: async () => {
      await mutate();
      await mutateDocuments();
    },
  });

  // Check if all required documents are approved
  const allDocumentsApproved = useMemo(() => {
    if (!documentsData?.data) return false;

    const documents = documentsData.data;
    const approvedTypes = new Set(
      documents
        .filter((doc) => doc.status === DocumentDtoStatusEnum.Approved)
        .map((doc) => doc.type)
    );

    return REQUIRED_DOCUMENTS.every((requiredType) =>
      approvedTypes.has(requiredType)
    );
  }, [documentsData]);

  useEffect(() => {
    if (!user) return;
    if (user.role === Role.Admin || import.meta.env.VITE_USE_MOCKS === 'true') {
      navigate(AppRoutes.INDEX, { replace: true });
    }
  }, [user, navigate]);

  // Redirect to app if all documents are approved
  useEffect(() => {
    if (user && hasAllDocumentsUploaded(user) && allDocumentsApproved) {
      showSuccessToast("All documents approved! Redirecting to app...");
      navigate(AppRoutes.INDEX, { replace: true });
    }
  }, [user, navigate, allDocumentsApproved]);

  if (isLoading || isLoadingDocuments) {
    return (
      <AuthLayout headerTitle="Loading...">
        <Loader />
      </AuthLayout>
    );
  }

  if (!user) {
    return (
      <AuthLayout headerTitle="Error">
        <div className="text-center">
          <Text variant="body" className="text-accentError">
            Unable to load user data
          </Text>
        </div>
      </AuthLayout>
    );
  }

  // Redirect admin users away from onboarding
  if (user.role === Role.Admin) {
    return (
      <AuthLayout headerTitle="Redirecting...">
        <div className="text-center py-8 w-full">
          <Loader />
          <Text variant="body" tone="body" className="mt-4">
            Redirecting...
          </Text>
        </div>
      </AuthLayout>
    );
  }

  // If all documents are uploaded but not all approved, show waiting state
  if (hasAllDocumentsUploaded(user) && !allDocumentsApproved) {
    const documents = documentsData?.data || [];
    const requiredDocuments = documents.filter((doc) =>
      REQUIRED_DOCUMENTS.includes(doc.type)
    );

    return (
      <AuthLayout
        headerTitle="Waiting for Approval"
        headerSubtitle="Your documents are being reviewed by an admin"
      >
        <div className="w-full space-y-6">
          <div>
            <Text variant="bodyLg" tone="dark" className="mb-2">
              All documents have been uploaded successfully!
            </Text>
            <Text variant="body" tone="body">
              Please wait while an administrator reviews and approves your
              documents. You will be able to access the platform once all
              documents are approved.
            </Text>
          </div>

          <DocumentsSection
            documents={requiredDocuments}
            showOnlyRequired={true}
            requiredTypes={REQUIRED_DOCUMENTS}
          />

          <div className="pt-4 border-t border-uiSecondary flex flex-col items-center gap-4">
            <Text variant="bodyXs" tone="body">
              You will be automatically redirected once all documents are
              approved.
            </Text>
            <Button variant="secondary" onClick={handleLogout}>Logout</Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // If all documents are uploaded and approved, show loading while redirecting
  if (hasAllDocumentsUploaded(user) && allDocumentsApproved) {
    return (
      <AuthLayout headerTitle="Onboarding Complete">
        <div className="text-center py-8 w-full">
          <Loader />
          <Text variant="body" tone="body" className="mt-4">
            Redirecting to app...
          </Text>
        </div>
      </AuthLayout>
    );
  }

  const missingDocuments = getMissingDocuments(user);
  const uploadedDocuments = user.documents?.filter((doc) => doc.uploaded) || [];

  return (
    <AuthLayout
      headerTitle="Complete Your Onboarding"
      headerSubtitle="Upload the required documents to get started"
    >
      <div className="w-full space-y-6">
        <div>
          <Text variant="bodyLg" tone="dark" className="mb-2">
            Welcome! To get started, please upload the required documents.
          </Text>
          <Text variant="body" tone="body">
            All documents must be uploaded before you can access the full
            platform.
          </Text>
        </div>

        <div className="space-y-4">
          <UploadedDocumentsSection documents={uploadedDocuments} />
          <RequiredDocumentsSection
            documentTypes={missingDocuments}
            uploading={uploading}
            uploadProgress={uploadProgress}
            selectedFiles={selectedFiles}
            onFileSelect={handleFileSelect}
            onFileRemove={handleFileRemove}
            onConfirmUpload={handleConfirmUpload}
          />
        </div>

        <div className="pt-4 border-t border-uiSecondary flex flex-col items-center gap-4">
          <Text variant="bodyXs" tone="body">
            Supported formats: PDF, JPG, JPEG, PNG
          </Text>
          <Button variant="secondary" onClick={handleLogout}>Logout</Button>
        </div>
      </div>
    </AuthLayout>
  );
}
